import * as SliderPrimitive from '@radix-ui/react-slider';
import { cva } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '../../styles/lib/utils';
import { Icon } from '../Icon';
import { Label } from '../Label';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';

const sliderTrackVariants = cva('relative h-1 w-full grow rounded-full', {
  variants: {
    disabled: {
      true: 'bg-grey-20',
      false: 'bg-grey-30',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const sliderTickVariants = cva('h-full border', {
  variants: {
    disabled: {
      true: 'border-grey-20',
      false: 'border-grey-30',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const sliderRangeVariants = cva('absolute h-full rounded-full', {
  variants: {
    disabled: {
      true: 'bg-grey-40',
      false: 'bg-slate-70',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

const sliderThumbVariants = cva('block h-5 w-5 rounded-full focus-ring', {
  variants: {
    disabled: {
      true: 'cursor-not-allowed bg-grey-40',
      false: 'cursor-pointer bg-slate-70',
    },
  },
  defaultVariants: {
    disabled: false,
  },
});

type SliderProps = Omit<
  React.ComponentProps<typeof SliderPrimitive.Root>,
  'min' | 'max' | 'value' | 'defaultValue' | 'onValueChange'
> & {
  min: number;
  max: number;
  value: number;
  onValueChange: (value: number) => void;
  label: string;
  labelClassName?: string;
  labelGroupClassName?: string;
  valueClassName?: string;
  tooltipText?: string;
  formatValue?: (value: number) => string;
  'data-testid'?: string;
  showTickMarks?: boolean;
};

const Slider = React.forwardRef<React.ElementRef<typeof SliderPrimitive.Root>, SliderProps>(
  (
    {
      className,
      label,
      labelClassName,
      labelGroupClassName,
      valueClassName,
      tooltipText,
      formatValue = (value) => String(value),
      disabled,
      value,
      min,
      max,
      showTickMarks = false,
      onValueChange,
      'data-testid': dataTestId,
      ...props
    },
    ref,
  ) => {
    const labelId = React.useId();

    const handleValueChange = ([next]: number[]) => {
      onValueChange(next);
    };

    const tooltipTestId = dataTestId ? `${dataTestId}-tooltip` : undefined;
    const thumbTestId = dataTestId ? `${dataTestId}-thumb` : undefined;

    const tickMarks = React.useMemo(() => {
      if (!showTickMarks) return null;

      const ticks: React.JSX.Element[] = [];
      for (let i = min; i <= max; i++) {
        ticks.push(<span key={`i-${i}`} data-slot="slider-tick" className={cn(sliderTickVariants({ disabled }))} />);
      }

      return (
        <div data-slot="slider-ticks" className="absolute -top-1 flex h-3 w-full justify-between px-2">
          {ticks}
        </div>
      );
    }, [showTickMarks, disabled, min, max]);

    return (
      <div className={cn('flex w-full flex-col gap-2', className)} data-slot="slider">
        <div className={cn('flex items-center shrink-0 gap-1', labelGroupClassName)} data-slot="slider-label">
          <Label id={labelId} className={cn('text-xs font-medium text-grey-80', labelClassName)}>
            {label}
          </Label>
          {tooltipText && (
            <Tooltip
              text={tooltipText}
              delayDuration={200}
              sideOffset={10}
              aria-label={`About ${label}`}
              triggerClassName="flex rounded-sm"
              data-testid={tooltipTestId}
            >
              <Icon icon="questionMarkCircled" size={12} className="text-primary" />
            </Tooltip>
          )}
        </div>
        <div className="flex w-full items-center justify-between gap-2">
          <SliderPrimitive.Root
            ref={ref}
            data-slot="slider-root"
            data-testid={dataTestId}
            className="relative flex flex-1 touch-none select-none items-center"
            disabled={disabled}
            min={min}
            max={max}
            value={[value]}
            onValueChange={handleValueChange}
            {...props}
          >
            {tickMarks}

            <SliderPrimitive.Track data-slot="slider-track" className={cn(sliderTrackVariants({ disabled }))}>
              <SliderPrimitive.Range data-slot="slider-range" className={cn(sliderRangeVariants({ disabled }))} />
            </SliderPrimitive.Track>
            <SliderPrimitive.Thumb
              data-slot="slider-thumb"
              data-testid={thumbTestId}
              aria-label={label}
              aria-labelledby={labelId}
              aria-valuetext={formatValue(value)}
              className={cn(sliderThumbVariants({ disabled }))}
            />
          </SliderPrimitive.Root>
          <Text
            className={cn('shrink-0 font-semibold text-right min-w-[3em]', valueClassName)}
            data-slot="slider-value"
          >
            {formatValue(value)}
          </Text>
        </div>
      </div>
    );
  },
);

export { Slider };
export type { SliderProps };
